"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  layoutMultiplication,
  type PlaceholderStyle,
} from "@/lib/tool-kit/arithmetic/long-multiplication";
import {
  closingLines,
  computeMultiplication,
  hasSkippedZero,
  type MultiplicationComputation,
  type MultiplicationView,
  workingSections,
} from "@/lib/tool-kit/arithmetic/multiplication-view";

/** English strings. A translated content file overrides these through `ui`. */
export const LONG_MULTIPLICATION_CALCULATOR_STRINGS = {
  firstLabel: "First number (multiplicand)",
  secondLabel: "Second number (multiplier)",
  numberHint: "Whole numbers or decimals, up to 25 digits each.",
  placeholderLabel: "Shifted rows",
  optionZeros: "Placeholder zeros",
  optionBlank: "Blank spaces",
  showCarries: "Show the carries",
  results: "Results",
  product: "Product",
  estimate: "Estimate to check",
  estimateValue: "{a} × {b} ≈ {product}",
  decimalPlaces: "Decimal places",
  digits: "Digits in the product",
  diagram: "Long multiplication layout",
  diagramLabel: "Long multiplication of {a} by {b}, product {product}",
  stepsHeading: "Step by step",
  ignorePoints:
    "Ignore the decimal points while multiplying: {a} × {b} becomes {wa} × {wb}.",
  rowHeading:
    "Row {n}: multiply {multiplicand} by {digit}, the digit in the {place} place.",
  placeNames: [
    "ones",
    "tens",
    "hundreds",
    "thousands",
    "ten-thousands",
    "hundred-thousands",
    "millions",
    "ten-millions",
    "hundred-millions",
    "billions",
  ],
  placeFallback: "10^{power}",
  placeholderZeros:
    "This digit is in the {place} place, so start the row with {count} as placeholders.",
  placeholderBlank:
    "This digit is in the {place} place, so shift the row {count} to the left.",
  shiftPlaces: "one: {count} place|other: {count} places",
  zerosCount: "one: {count} zero|other: {count} zeros",
  mulLine: "{digit} × {m} = {product}",
  mulLineCarry: "{digit} × {m} + {carryIn} (carried) = {product}",
  writeCarry: "write {write}, carry {carry}",
  writeOnly: "write {write}",
  writeAll: "write all of {product} in front",
  rowResult: "The row is worth {value}.",
  skipZero:
    "The second number has a 0 digit. That row would be all zeros, so it is left out.",
  onlyRow: "There is only one row, so it is the product.",
  addRows: "Add the rows: {sum} = {total}.",
  pointRule:
    "Count the decimal places in the two numbers: {scale} in total. Put the decimal point {scale} places from the right of {digits}: {result}.",
  zeroProduct: "Anything times zero is zero, so no rows are needed.",
  signRule: "Exactly one number is negative, so the product is negative.",
  enterBoth: "Enter two numbers to multiply.",
  invalid: "Enter plain numbers such as 1234, 12.5 or −7.",
  tooLong: "Use numbers of at most 25 digits each.",
  copy: "Copy product",
  copied: "Copied!",
  copyText: "{a} × {b} = {product}",
} as const;

type Strings = typeof LONG_MULTIPLICATION_CALCULATOR_STRINGS;

const PRE_CLASS =
  "overflow-x-auto rounded-lg border border-border bg-muted/30 p-4 font-mono text-foreground text-sm leading-snug";
const LIST_CLASS = "list-disc space-y-1 pl-6 text-foreground text-sm";

function Working({
  view,
  placeholder,
  carries,
  strings,
  locale,
}: {
  view: MultiplicationView;
  placeholder: PlaceholderStyle;
  carries: boolean;
  strings: Strings;
  locale: string;
}) {
  const { multiplication: m } = view;
  const lines = layoutMultiplication(m, { placeholder, carries });
  const sections = workingSections(m, strings, locale, placeholder);
  const closing = closingLines(m, strings);
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.diagram}</h3>
      <pre
        aria-label={t(strings.diagramLabel, {
          a: m.multiplicandText,
          b: m.multiplierText,
          product: m.productTrimmed,
        })}
        className={PRE_CLASS}
        role="img"
      >
        {lines.join("\n")}
      </pre>
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      {m.scale > 0 ? (
        <p className="text-foreground text-sm">
          {t(strings.ignorePoints, {
            a: m.multiplicandText,
            b: m.multiplierText,
            wa: m.multiplicand,
            wb: m.multiplier,
          })}
        </p>
      ) : null}
      {sections.map((section) => (
        <div className="space-y-1" key={section.heading}>
          <p className="font-medium text-foreground text-sm">
            {section.heading}
          </p>
          {section.note ? (
            <p className="text-muted-foreground text-sm">{section.note}</p>
          ) : null}
          <ul className={LIST_CLASS}>
            {section.lines.map((line, index) => (
              // The same arithmetic line can recur within a row.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional steps
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
      ))}
      {hasSkippedZero(m) ? (
        <p className="text-muted-foreground text-sm">{strings.skipZero}</p>
      ) : null}
      {closing.map((line) => (
        <p className="text-foreground text-sm" key={line}>
          {line}
        </p>
      ))}
    </>
  );
}

function Problem({
  kind,
  strings,
}: {
  kind: "empty" | "invalid" | "too-long";
  strings: Strings;
}) {
  if (kind === "empty") {
    return <Notice>{strings.enterBoth}</Notice>;
  }
  return (
    <ErrorNotice>
      {kind === "invalid" ? strings.invalid : strings.tooLong}
    </ErrorNotice>
  );
}

function Outcome({
  outcome,
  placeholder,
  carries,
  strings,
  locale,
}: {
  outcome: MultiplicationComputation;
  placeholder: PlaceholderStyle;
  carries: boolean;
  strings: Strings;
  locale: string;
}) {
  if (outcome.kind !== "ok") {
    return <Problem kind={outcome.kind} strings={strings} />;
  }
  const { view } = outcome;
  const m = view.multiplication;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.product} value={m.productTrimmed} />
        <StatCard
          label={strings.estimate}
          value={t(strings.estimateValue, {
            a: view.estimate.a,
            b: view.estimate.b,
            product: view.estimate.product,
          })}
        />
        <StatCard label={strings.digits} value={m.productDigits.length} />
        {m.scale > 0 ? (
          <StatCard label={strings.decimalPlaces} value={m.scale} />
        ) : null}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={t(strings.copyText, {
          a: m.multiplicandText,
          b: m.multiplierText,
          product: m.productTrimmed,
        })}
      />
      <Working
        carries={carries}
        locale={locale}
        placeholder={placeholder}
        strings={strings}
        view={view}
      />
    </>
  );
}

export default function LongMultiplicationCalculatorWidget() {
  const strings = useToolUi(LONG_MULTIPLICATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const [first, setFirst] = useState("1234");
  const [second, setSecond] = useState("56");
  const [placeholder, setPlaceholder] = useState<PlaceholderStyle>("zeros");
  const [carries, setCarries] = useState(true);
  const outcome = useMemo(
    () => computeMultiplication(first, second),
    [first, second]
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.numberHint}
          id="lm-first"
          label={strings.firstLabel}
          mono
          numeric
          onChange={setFirst}
          value={first}
        />
        <TextField
          id="lm-second"
          label={strings.secondLabel}
          mono
          numeric
          onChange={setSecond}
          value={second}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.placeholderLabel}
          name="lm-placeholder"
          onChange={setPlaceholder}
          options={[
            { value: "zeros", label: strings.optionZeros },
            { value: "blank", label: strings.optionBlank },
          ]}
          value={placeholder}
        />
        <div className="self-end">
          <Checkbox
            checked={carries}
            label={strings.showCarries}
            onChange={setCarries}
          />
        </div>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          carries={carries}
          locale={locale}
          outcome={outcome}
          placeholder={placeholder}
          strings={strings}
        />
      </section>
    </>
  );
}
